import { createRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { withPermission } from "../lib/with-permission";
import { rootRoute } from "./__root";
import { PageHeader } from "../components/ui/PageHeader/PageHeader";
import { Card } from "../components/ui/Card/Card";
import { Table } from "../components/ui/Table/Table";
import { Modal } from "../components/ui/Modal/Modal";
import { LoadingSpinner } from "../components/ui/LoadingSpinner/LoadingSpinner";
import { ErrorFallback } from "../components/ui/ErrorFallback/ErrorFallback";
import { Pagination } from "../components/ui/Pagination/Pagination";
import { FilterBar } from "../components/ui/FilterBar/FilterBar";
import { SalesDocDetailView } from "../components/RelationshipMapModal/SalesDocDetailView";
import { useDeliveryList, deliveryDetailUrl, type Delivery } from "../hooks/use-deliveries";
import { formatAmount, formatDate } from "../lib/date";
import styles from "./Deliveries.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/deliveries",
	component: withPermission(DeliveriesPage, "deliveries", "read"),
	validateSearch: (search: Record<string, unknown>) => ({
		page: Number(search.page) || 1,
		size: Number(search.size) || 20,
		status: String(search.status || ""),
		docNo: String(search.docNo || ""),
		customer: String(search.customer || ""),
		orderNo: String(search.orderNo || ""),
		comments: String(search.comments || ""),
		docDateFrom: String(search.docDateFrom || ""),
		docDateTo: String(search.docDateTo || ""),
	}),
});

const STATUS_COLORS: Record<Delivery["status"], string> = {
	open: "#22c55e",
	closed: "#6b7280",
	cancelled: "#ef4444",
};

function DeliveriesPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const searchParams = route.useSearch();
	const { page, size: pageSize, status, docNo, customer, orderNo, comments } = searchParams;
	const { docDateFrom, docDateTo } = searchParams;
	const [selected, setSelected] = useState<Delivery | null>(null);

	const { data, isLoading, isError, refetch } = useDeliveryList({
		from: docDateFrom || undefined,
		to: docDateTo || undefined,
		status: status || undefined,
		docNo: docNo || undefined,
		customer: customer || undefined,
		orderNo: orderNo || undefined,
		comments: comments || undefined,
		page,
		limit: pageSize,
	});

	const statusLabel = (s: Delivery["status"]) =>
		s === "cancelled" ? t("cancelled") : s === "closed" ? t("closed") : t("open");
	const selectedOrderId = selected?.orders[0]?.id ?? null;

	return (
		<div className={styles.page}>
			<PageHeader title={t("deliveries")} />
			<div className={styles.filters}>
				<FilterBar
					stretch
					filters={[
						{ key: "docNo", type: "text", label: t("deliveryNo"), width: 130 },
						{ key: "customer", type: "text", label: t("counterpart"), width: 220 },
						{ key: "orderNo", type: "text", label: t("orderNumber"), width: 130 },
						{ key: "comments", type: "text", label: t("remarks"), width: 200 },
						{
							key: "status",
							type: "select",
							label: t("all"),
							options: [
								{ value: "open", label: t("open") },
								{ value: "closed", label: t("closed") },
								{ value: "cancelled", label: t("cancelled") },
							],
						},
						{ key: "docDate", type: "dateRange", label: t("postingDate") },
					]}
					values={{ docNo, customer, orderNo, comments, status, docDateFrom, docDateTo }}
					onChange={(vals) =>
						navigate({
							to: ".",
							search: {
								docNo: vals.docNo || "",
								customer: vals.customer || "",
								orderNo: vals.orderNo || "",
								comments: vals.comments || "",
								status: vals.status || "",
								docDateFrom: vals.docDateFrom || "",
								docDateTo: vals.docDateTo || "",
								page: 1,
								size: pageSize,
							},
						})
					}
				/>
				{data && (
					<span className={styles.range}>
						{t("deliveriesRange", {
							from: formatDate(data.range.from),
							to: formatDate(data.range.to),
						})}
					</span>
				)}
			</div>

			<Card className={styles.cardFill ?? ""} bodyClassName={styles.cardBodyFill ?? ""}>
				{isLoading && <LoadingSpinner />}
				{isError && <ErrorFallback onRetry={refetch} />}
				{!isLoading && !isError && (
					<Table
						className={styles.tableScroll}
						onRowClick={setSelected}
						columns={[
							{
								key: "docNum",
								header: t("deliveryNo"),
								render: (row) => <strong className={styles.docNum}>{row.docNum}</strong>,
							},
							{
								key: "docDate",
								header: t("postingDate"),
								render: (row) => formatDate(row.docDate),
							},
							{
								key: "docDueDate",
								header: t("deliveryDate"),
								render: (row) => formatDate(row.docDueDate),
							},
							{
								key: "customer",
								header: t("counterpart"),
								render: (row) => (
									<>
										<div>{row.cardName}</div>
										<div className={styles.muted}>{row.cardCode}</div>
									</>
								),
							},
							{
								key: "numAtCard",
								header: t("reference"),
								render: (row) => row.numAtCard || "-",
							},
							{
								key: "orders",
								header: t("orderNumber"),
								render: (row) => row.orders.map((o) => o.number).join(", "),
							},
							{
								key: "salesPerson",
								header: t("salesPerson"),
								render: (row) => row.salesPerson || "-",
							},
							{
								key: "total",
								header: t("total"),
								render: (row) => `${formatAmount(row.docTotal)} ${row.docCur ?? ""}`,
							},
							{
								key: "status",
								header: t("status"),
								render: (row) => (
									<span style={{ color: STATUS_COLORS[row.status], fontWeight: 600 }}>
										{statusLabel(row.status)}
									</span>
								),
							},
							{
								key: "comments",
								header: t("remarks"),
								render: (row) => (
									<span className={styles.comments} title={row.comments ?? undefined}>
										{row.comments || "-"}
									</span>
								),
							},
						]}
						data={data?.items ?? []}
						keyExtractor={(row) => String(row.docEntry)}
					/>
				)}
				<Pagination
					page={page}
					pageSize={pageSize}
					total={data?.total ?? 0}
					onPageChange={(newPage) =>
						navigate({ to: ".", search: { ...searchParams, page: newPage } })
					}
					onPageSizeChange={(newSize) =>
						navigate({ to: ".", search: { ...searchParams, page: 1, size: newSize } })
					}
				/>
			</Card>

			<Modal
				isOpen={!!selected && !!selectedOrderId}
				onClose={() => setSelected(null)}
				title={selected ? `${t("deliveryNo")} ${selected.docNum}` : ""}
				maxWidth={1100}
			>
				{selected && selectedOrderId && (
					<SalesDocDetailView
						orderId={selectedOrderId}
						kind="delivery"
						docEntry={selected.docEntry}
						url={deliveryDetailUrl(selected.docEntry, selectedOrderId)}
					/>
				)}
			</Modal>
		</div>
	);
}
